import { Component, signal, computed, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subscription, combineLatest, filter, take } from 'rxjs';
import {
  IconComponent,
  ButtonComponent,
  HeroSectionComponent,
  HeroMetaItem,
  PageNavItem,
  FaqAccordionComponent,
  FaqItem as LibFaqItem,
  CtaSectionComponent,
  ChecklistComponent,
  ChecklistItem,
  InfoCardGridComponent,
  InfoCard,
} from 'htx-ui-library';
import { AuthService } from '../../../../services/auth/auth.service';
import { UseCaseParticipationService } from '../../../../services/use-case-participation/use-case-participation.service';
import { NotificationService } from '../../../../services/form/notification.service';
import { OrganizationService } from '../../../../services/organizations/organization.service';
import { AdmissionState, ActivityState } from '../../../../services/organizations/organization.model';


interface Role {
  icon: string;
  title: string;
  description: string;
  permissions: string[];
}

interface SoftwareComponent {
  icon: string;
  iconColor: 'teal' | 'blue' | 'green';
  title: string;
  description: string;
  status: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface UseCaseData {
  id: string;
  icon: string;
  titleKey: string;
  subtitleKey: string;
  rolesCount: number;
  componentsCount: number;
  descriptionKey: string;
  roles: Role[];
  software: SoftwareComponent[];
  prerequisites: string[];
  screenshots: { placeholder: string; caption: string }[];
  faq: FaqItem[];
}

@Component({
  selector: 'app-use-case-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    IconComponent,
    ButtonComponent,
    HeroSectionComponent,
    FaqAccordionComponent,
    CtaSectionComponent,
    ChecklistComponent,
    InfoCardGridComponent,
  ],
  templateUrl: './use-case-detail-page.component.html',
  styleUrl: './use-case-detail-page.component.scss'
})
export class UseCaseDetailPageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private transloco = inject(TranslocoService);
  private authService = inject(AuthService);
  private participationService = inject(UseCaseParticipationService);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private translation = toSignal(this.transloco.selectTranslation(), { initialValue: {} });
  private subscriptions = new Subscription();

  useCaseId = signal<string>('');
  isLoggedIn = this.authService.isLoggedIn$;
  requestInProgress = signal(false);
  participationState = signal<AdmissionState | null>(null);
  orgNotApproved = signal(false);
  orgInactive = signal(false);
  statusLoading = signal(true);
  statusError = signal(false);
  AdmissionState = AdmissionState;

  useCases: Record<string, UseCaseData> = {
    'supply-chain': {
      id: 'supply-chain',
      icon: 'account_tree',
      titleKey: 'usecases.supplyChain.title',
      subtitleKey: 'usecases.supplyChain.subtitle',
      rolesCount: 4,
      componentsCount: 3,
      descriptionKey: 'usecases.supplyChain.detailDescription',
      roles: [
        { icon: 'factory', title: 'usecases.supplyChain.roles.manufacturer.title', description: 'usecases.supplyChain.roles.manufacturer.description', permissions: ['usecases.supplyChain.roles.manufacturer.p1', 'usecases.supplyChain.roles.manufacturer.p2', 'usecases.supplyChain.roles.manufacturer.p3', 'usecases.supplyChain.roles.manufacturer.p4'] },
        { icon: 'local_shipping', title: 'usecases.supplyChain.roles.wholesaler.title', description: 'usecases.supplyChain.roles.wholesaler.description', permissions: ['usecases.supplyChain.roles.wholesaler.p1', 'usecases.supplyChain.roles.wholesaler.p2', 'usecases.supplyChain.roles.wholesaler.p3', 'usecases.supplyChain.roles.wholesaler.p4'] },
        { icon: 'local_pharmacy', title: 'usecases.supplyChain.roles.pharmacy.title', description: 'usecases.supplyChain.roles.pharmacy.description', permissions: ['usecases.supplyChain.roles.pharmacy.p1', 'usecases.supplyChain.roles.pharmacy.p2', 'usecases.supplyChain.roles.pharmacy.p3', 'usecases.supplyChain.roles.pharmacy.p4'] },
        { icon: 'admin_panel_settings', title: 'usecases.supplyChain.roles.admin.title', description: 'usecases.supplyChain.roles.admin.description', permissions: ['usecases.supplyChain.roles.admin.p1', 'usecases.supplyChain.roles.admin.p2', 'usecases.supplyChain.roles.admin.p3', 'usecases.supplyChain.roles.admin.p4'] }
      ],
      software: [
        { icon: 'hub', iconColor: 'teal', title: 'usecases.supplyChain.software.connector.title', description: 'usecases.supplyChain.software.connector.description', status: 'usecases.status.inDevelopment' },
        { icon: 'qr_code_scanner', iconColor: 'blue', title: 'usecases.supplyChain.software.serialization.title', description: 'usecases.supplyChain.software.serialization.description', status: 'usecases.status.inDevelopment' },
        { icon: 'dashboard', iconColor: 'green', title: 'usecases.supplyChain.software.dashboard.title', description: 'usecases.supplyChain.software.dashboard.description', status: 'usecases.status.inDevelopment' }
      ],
      prerequisites: [
        'usecases.supplyChain.prerequisites.1',
        'usecases.supplyChain.prerequisites.2',
        'usecases.supplyChain.prerequisites.3',
        'usecases.supplyChain.prerequisites.4',
        'usecases.supplyChain.prerequisites.5',
        'usecases.supplyChain.prerequisites.6'
      ],
      screenshots: [
        { placeholder: 'usecases.supplyChain.screenshots.1.placeholder', caption: 'usecases.supplyChain.screenshots.1.caption' },
        { placeholder: 'usecases.supplyChain.screenshots.2.placeholder', caption: 'usecases.supplyChain.screenshots.2.caption' },
        { placeholder: 'usecases.supplyChain.screenshots.3.placeholder', caption: 'usecases.supplyChain.screenshots.3.caption' }
      ],
      faq: [
        { question: 'usecases.supplyChain.faq.1.question', answer: 'usecases.supplyChain.faq.1.answer' },
        { question: 'usecases.supplyChain.faq.2.question', answer: 'usecases.supplyChain.faq.2.answer' },
        { question: 'usecases.supplyChain.faq.3.question', answer: 'usecases.supplyChain.faq.3.answer' },
        { question: 'usecases.supplyChain.faq.4.question', answer: 'usecases.supplyChain.faq.4.answer' },
        { question: 'usecases.supplyChain.faq.5.question', answer: 'usecases.supplyChain.faq.5.answer' }
      ]
    },
    'shortage-management': {
      id: 'shortage-management',
      icon: 'monitoring',
      titleKey: 'usecases.shortageManagement.title',
      subtitleKey: 'usecases.shortageManagement.subtitle',
      rolesCount: 4,
      componentsCount: 3,
      descriptionKey: 'usecases.shortageManagement.detailDescription',
      roles: [
        { icon: 'factory', title: 'usecases.shortageManagement.roles.manufacturer.title', description: 'usecases.shortageManagement.roles.manufacturer.description', permissions: ['usecases.shortageManagement.roles.manufacturer.p1', 'usecases.shortageManagement.roles.manufacturer.p2', 'usecases.shortageManagement.roles.manufacturer.p3', 'usecases.shortageManagement.roles.manufacturer.p4'] },
        { icon: 'local_shipping', title: 'usecases.shortageManagement.roles.wholesaler.title', description: 'usecases.shortageManagement.roles.wholesaler.description', permissions: ['usecases.shortageManagement.roles.wholesaler.p1', 'usecases.shortageManagement.roles.wholesaler.p2', 'usecases.shortageManagement.roles.wholesaler.p3', 'usecases.shortageManagement.roles.wholesaler.p4'] },
        { icon: 'local_pharmacy', title: 'usecases.shortageManagement.roles.pharmacy.title', description: 'usecases.shortageManagement.roles.pharmacy.description', permissions: ['usecases.shortageManagement.roles.pharmacy.p1', 'usecases.shortageManagement.roles.pharmacy.p2', 'usecases.shortageManagement.roles.pharmacy.p3', 'usecases.shortageManagement.roles.pharmacy.p4'] },
        { icon: 'account_balance', title: 'usecases.shortageManagement.roles.authority.title', description: 'usecases.shortageManagement.roles.authority.description', permissions: ['usecases.shortageManagement.roles.authority.p1', 'usecases.shortageManagement.roles.authority.p2', 'usecases.shortageManagement.roles.authority.p3', 'usecases.shortageManagement.roles.authority.p4'] }
      ],
      software: [
        { icon: 'crisis_alert', iconColor: 'teal', title: 'usecases.shortageManagement.software.earlyWarning.title', description: 'usecases.shortageManagement.software.earlyWarning.description', status: 'usecases.status.inDevelopment' },
        { icon: 'swap_horiz', iconColor: 'blue', title: 'usecases.shortageManagement.software.allocation.title', description: 'usecases.shortageManagement.software.allocation.description', status: 'usecases.status.inDevelopment' },
        { icon: 'analytics', iconColor: 'green', title: 'usecases.shortageManagement.software.reporting.title', description: 'usecases.shortageManagement.software.reporting.description', status: 'usecases.status.inDevelopment' }
      ],
      prerequisites: [
        'usecases.shortageManagement.prerequisites.1',
        'usecases.shortageManagement.prerequisites.2',
        'usecases.shortageManagement.prerequisites.3',
        'usecases.shortageManagement.prerequisites.4',
        'usecases.shortageManagement.prerequisites.5'
      ],
      screenshots: [
        { placeholder: 'usecases.shortageManagement.screenshots.1.placeholder', caption: 'usecases.shortageManagement.screenshots.1.caption' },
        { placeholder: 'usecases.shortageManagement.screenshots.2.placeholder', caption: 'usecases.shortageManagement.screenshots.2.caption' },
        { placeholder: 'usecases.shortageManagement.screenshots.3.placeholder', caption: 'usecases.shortageManagement.screenshots.3.caption' }
      ],
      faq: [
        { question: 'usecases.shortageManagement.faq.1.question', answer: 'usecases.shortageManagement.faq.1.answer' },
        { question: 'usecases.shortageManagement.faq.2.question', answer: 'usecases.shortageManagement.faq.2.answer' },
        { question: 'usecases.shortageManagement.faq.3.question', answer: 'usecases.shortageManagement.faq.3.answer' },
        { question: 'usecases.shortageManagement.faq.4.question', answer: 'usecases.shortageManagement.faq.4.answer' },
        { question: 'usecases.shortageManagement.faq.5.question', answer: 'usecases.shortageManagement.faq.5.answer' }
      ]
    }
  };

  useCase = computed(() => this.useCases[this.useCaseId()] || null);

  private t(key: string): string {
    return this.transloco.translate(key);
  }

  heroMetaItems = computed<HeroMetaItem[]>(() => {
    this.translation();
    const uc = this.useCase();
    if (!uc) return [];
    return [
      { icon: 'groups', text: `${uc.rolesCount} ${this.t('usecases.meta.roles')}` },
      { icon: 'apps', text: `${uc.componentsCount} ${this.t('usecases.meta.components')}` },
      { icon: 'verified', text: this.t('usecases.status.active') },
    ];
  });

  pageNavItems = computed<PageNavItem[]>(() => {
    this.translation();
    return [
      { icon: 'description', label: this.t('usecases.sections.description'), targetId: 'beschreibung' },
      { icon: 'people', label: this.t('usecases.sections.roles'), targetId: 'rollen' },
      { icon: 'integration_instructions', label: this.t('usecases.sections.software'), targetId: 'software' },
      { icon: 'checklist', label: this.t('usecases.sections.prerequisites'), targetId: 'voraussetzungen' },
      { icon: 'image', label: this.t('usecases.sections.screenshots'), targetId: 'screenshots' },
      { icon: 'help_outline', label: this.t('usecases.sections.faq'), targetId: 'faq' },
    ];
  });

  roleCards = computed<InfoCard[]>(() => {
    this.translation();
    const uc = this.useCase();
    if (!uc) return [];
    return uc.roles.map(role => ({
      icon: role.icon,
      title: this.t(role.title),
      description: this.t(role.description),
      tags: role.permissions.map(p => this.t(p)),
    }));
  });

  softwareCards = computed<InfoCard[]>(() => {
    this.translation();
    const uc = this.useCase();
    if (!uc) return [];
    return uc.software.map(sw => ({
      icon: sw.icon,
      iconColor: sw.iconColor,
      title: this.t(sw.title),
      description: this.t(sw.description),
      status: this.t(sw.status),
    }));
  });

  prerequisiteItems = computed<ChecklistItem[]>(() => {
    this.translation();
    const uc = this.useCase();
    if (!uc) return [];
    return uc.prerequisites.map(p => ({ text: this.t(p) }));
  });

  faqItems = computed<LibFaqItem[]>(() => {
    this.translation();
    const uc = this.useCase();
    if (!uc) return [];
    return uc.faq.map(f => ({
      question: this.t(f.question),
      answer: this.t(f.answer),
    }));
  });

  ngOnInit(): void {
    // Set useCaseId from route
    this.subscriptions.add(
      this.route.params.subscribe(params => {
        this.useCaseId.set(params['id'] || '');
      })
    );

    // Wait for both route params and auth to be ready
    this.subscriptions.add(
      combineLatest([
        this.route.params,
        this.isLoggedIn.pipe(filter(v => v), take(1))
      ]).subscribe(([params]) => {
        this.useCaseId.set(params['id'] || '');
        this.statusLoading.set(true);
        this.loadOrgStatus();
        this.loadParticipationStatus();
      })
    );

    // If user is not logged in after initial check, stop loading
    this.subscriptions.add(
      this.isLoggedIn.pipe(take(1)).subscribe(loggedIn => {
        if (!loggedIn) {
          this.statusLoading.set(false);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadOrgStatus(): void {
    this.organizationService.getMyOrganization().subscribe({
      next: (org) => {
        this.orgNotApproved.set(org.admissionState !== AdmissionState.APPROVED);
        this.orgInactive.set(org.activityState === ActivityState.INACTIVE);
      },
      error: () => this.orgNotApproved.set(true)
    });
  }

  private loadParticipationStatus(): void {
    if (!this.useCaseId()) {
      this.statusLoading.set(false);
      return;
    }
    this.participationService.getMyStatus(this.useCaseId()).subscribe({
      next: (status) => {
        this.participationState.set(status?.admissionState ?? null);
        this.statusError.set(false);
        this.statusLoading.set(false);
      },
      error: () => {
        this.statusError.set(true);
        this.statusLoading.set(false);
      }
    });
  }

  scrollTo(targetId: string): void {
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onRequestParticipation(): void {
    if (this.requestInProgress() || this.participationState()) return;
    this.requestInProgress.set(true);

    this.participationService.requestParticipation(this.useCaseId()).subscribe({
      next: () => {
        this.participationState.set(AdmissionState.PENDING);
        this.notificationService.showSuccess('usecases.cta.success');
        this.requestInProgress.set(false);
      },
      error: () => {
        this.notificationService.showError('usecases.cta.error');
        this.requestInProgress.set(false);
      }
    });
  }
}
